import React, { useState, useMemo, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import BlankDropZone from './BlankDropZone';
import OptionBlock from './OptionBlock';
import QuestionAudioPlayer from '../shared/QuestionAudioPlayer';
import {
  getAvailableOptions,
  parseSentenceWithBlanks,
  findFirstEmptyBlank,
  checkAllBlanksFilled,
  validateAnswers
} from './fibHelpers';

// Get base URL for audio
const getBaseURL = () => {
  return process.env.REACT_APP_API_URL || 'http://localhost:3000';
};

const FillInTheBlank = ({ question, onAnswer, onNext, sessionData = null, variant = 'default' }) => {
  // Transform backend data to component format
  const transformedQuestion = useMemo(() => {
    const blanks = [];
    
    // Priority: use blank-passage if available
    const text = question['blank-passage'] || question.text || question['text-soal'] || '';
    
    // Parse [blank-1], [blank-2], etc. from blank-passage
    const blankMatches = text.match(/\[blank-\d+\]/g) || [];
    
    if (blankMatches.length > 0 && question.choices && Array.isArray(question.choices)) {
      // Create maps: idx -> text and text -> idx
      const choicesMap = {}; // idx -> text
      const textToIdxMap = {}; // text -> idx
      question.choices.forEach((choice) => {
        const idx = choice.idx !== undefined ? choice.idx : choice.id;
        const choiceText = choice.text || choice['option-text'] || '';
        choicesMap[idx] = choiceText;
        textToIdxMap[choiceText] = idx;
      });
      
      if (process.env.NODE_ENV === 'development') {
        // console.log('🔍 FIB - Created maps:', {
        //   choices: question.choices,
        //   choicesMap,
        //   textToIdxMap,
        // });
      }
      
      // Get all options (all choices text) for display
      const allOptions = question.choices
        .map(choice => choice.text || choice['option-text'] || '')
        .filter(Boolean);
      
      // Map answers array to correct answers using choices idx
      const answers = question.answers || question['correct-answers'] || [];
      
      blankMatches.forEach((placeholder, index) => {
        // Extract blank number from [blank-1], [blank-2], etc.
        const blankNumberMatch = placeholder.match(/\[blank-(\d+)\]/);
        const blankNumber = blankNumberMatch ? parseInt(blankNumberMatch[1]) : index + 1;
        const blankId = blankNumber;
        
        // Get correct answer idx: answers[index] is the idx of the correct choice
        const correctIdx = Array.isArray(answers) ? answers[index] : null;
        
        blanks.push({
          id: blankId,
          options: allOptions, // For display
          correctIdx: correctIdx, // Store idx for validation
          correctAnswer: correctIdx !== null && choicesMap[correctIdx] ? choicesMap[correctIdx] : '', // For display/validation fallback
        });
      });
    } else if (question.blanks && Array.isArray(question.blanks)) {
      // If blanks already structured
      question.blanks.forEach((blank, index) => {
        blanks.push({
          id: blank.id || blank['blank-id'] || index + 1,
          options: blank.options || blank.choices || [],
          correctAnswer: blank['correct-answer'] || blank.correctAnswer || blank.answer || '',
        });
      });
    } else if (question.choices && Array.isArray(question.choices)) {
      // Fallback: try {blank1} format
      const blankMatchesFallback = text.match(/\{blank\d+\}/g) || [];
      
      if (blankMatchesFallback.length > 0) {
        const allOptions = question.choices.map(choice => 
          choice.text || choice['option-text'] || ''
        ).filter(Boolean);
        
        const correctAnswers = question.answers || question['correct-answers'] || [];
        
        blankMatchesFallback.forEach((placeholder, index) => {
          const blankId = index + 1;
          const correctAnswer = Array.isArray(correctAnswers) 
            ? (correctAnswers[index] || correctAnswers[blankId - 1] || '')
            : '';
          
          blanks.push({
            id: blankId,
            options: allOptions,
            correctAnswer: correctAnswer,
          });
        });
      }
    }

    // Create maps once for reuse
    const choicesMap = question.choices ? (() => {
      const map = {};
      question.choices.forEach((choice) => {
        const idx = choice.idx !== undefined ? choice.idx : choice.id;
        const choiceText = choice.text || choice['option-text'] || '';
        map[idx] = choiceText;
      });
      return map;
    })() : {};
    
    const textToIdxMap = question.choices ? (() => {
      const map = {};
      question.choices.forEach((choice) => {
        const idx = choice.idx !== undefined ? choice.idx : choice.id;
        const choiceText = choice.text || choice['option-text'] || '';
        map[choiceText] = idx;
      });
      return map;
    })() : {};
    
    if (process.env.NODE_ENV === 'development') {
      // console.log('🔍 FIB - Final maps in return:', {
      //   choices: question.choices,
      //   choicesMap,
      //   textToIdxMap,
      // });
    }

    return {
      ...question,
      blanks: blanks,
      text: text,
      choicesMap: choicesMap,
      textToIdxMap: textToIdxMap,
    };
  }, [question]);
  const [userAnswers, setUserAnswers] = useState({});
  const [draggedOption, setDraggedOption] = useState(null);
  const [dragOverBlank, setDragOverBlank] = useState(null);
  const activeAnnotationRef = useRef(null);
  const annotationAudioRef = useRef(null);

  // Reset state when question changes
  useEffect(() => {
    setUserAnswers({});
    setDraggedOption(null);
    setDragOverBlank(null);
    if (annotationAudioRef.current) {
      annotationAudioRef.current.pause();
      annotationAudioRef.current = null;
    }
  }, [question['soal-id'] || question.id]);

  // Build annotations map from question.annotations array
  const annotationsMap = useMemo(() => {
    const map = {};
    
    // First, try to get annotations from question.annotations array
    if (question.annotations && Array.isArray(question.annotations)) {
      question.annotations.forEach((annotation) => {
        const annId = annotation['ann-id'] || annotation.annId || annotation.id;
        const streamUrl = annotation['stream-url'] || annotation.streamUrl || annotation.stream_url;
        const annType = annotation['ann-type'] || annotation.annType || annotation.type;
        
        // If streamUrl exists, treat it as audio annotation (for new format without ann-type)
        if (annId && streamUrl) {
          map[annId] = {
            annId,
            streamUrl,
            annType: annType || 'audio',
          };
        }
      });
    }
    
    // Also parse HTML for data-ann-id attributes (fallback)
    const html = transformedQuestion.text || '';
    if (html && typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined') {
      try {
        const parser = new window.DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const nodes = doc.querySelectorAll('[data-ann-id]');

        nodes.forEach((node) => {
          const annId = node.getAttribute('data-ann-id');
          const annPath = node.getAttribute('data-ann-path');
          const annType = node.getAttribute('data-ann-type');

          // Only add if not already in map (from annotations array)
          // If annPath exists, treat it as audio (for new format)
          if (annId && annPath && !map[annId]) {
            // Convert data-ann-path to streamUrl
            const baseURL = getBaseURL();
            const streamUrl = `${baseURL}/${annPath}`;
            
            map[annId] = {
              annId,
              streamUrl,
              annType: annType || 'audio',
            };
          }
        });
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('FillInTheBlank: failed to parse annotations from HTML', error);
        }
      }
    }

    return map;
  }, [question.annotations, transformedQuestion.text]);

  const questionLevelAudioUrl = question?.['audio-url'] || question?.audio_url || null;

  const firstAnnotationAudioUrl = useMemo(() => {
    const html = transformedQuestion.text || '';
    if (!html || typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return null;
    try {
      const doc = new window.DOMParser().parseFromString(html, 'text/html');
      const nodes = doc.querySelectorAll('[data-ann-id]');
      for (const node of nodes) {
        const annId = node.getAttribute('data-ann-id');
        if (annId && annotationsMap[annId]?.streamUrl) {
          return annotationsMap[annId].streamUrl;
        }
      }
    } catch {}
    return null;
  }, [transformedQuestion.text, annotationsMap]);

  const displayAudioUrl = questionLevelAudioUrl || firstAnnotationAudioUrl;

  // Enhance HTML with annotation classes; hide annotation shown via waveform player
  const enhancedHtml = useMemo(() => {
    const html = transformedQuestion.text || '';
    if (!html || typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
      return html;
    }

    try {
      const parser = new window.DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const nodes = doc.querySelectorAll('[data-ann-id]');

      nodes.forEach((node) => {
        const annId = node.getAttribute('data-ann-id');
        if (annId && annotationsMap[annId]) {
          const isShownAsWaveform = !questionLevelAudioUrl && annotationsMap[annId].streamUrl === firstAnnotationAudioUrl;
          if (isShownAsWaveform) {
            node.style.display = 'none';
            // Remove trailing <br> / empty text nodes so they don't render as phantom gap
            let next = node.nextSibling;
            while (next && (next.nodeName === 'BR' || (next.nodeType === 3 && next.textContent.trim() === ''))) {
              const toRemove = next;
              next = next.nextSibling;
              toRemove.parentNode.removeChild(toRemove);
            }
          } else {
            node.classList.add('practice-annotation');
            node.classList.add('annotation-hidden');
            if (!node.hasAttribute('tabindex')) node.setAttribute('tabindex', '0');
            if (!node.hasAttribute('role')) node.setAttribute('role', 'button');
            node.setAttribute('aria-label', 'Play audio');
          }
        }
      });

      return doc.body.innerHTML;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('FillInTheBlank: failed to enhance HTML', error);
      }
      return html;
    }
  }, [transformedQuestion.text, annotationsMap, questionLevelAudioUrl, firstAnnotationAudioUrl]);

  const highlightAnnotation = (element) => {
    if (!element) return;

    if (activeAnnotationRef.current && activeAnnotationRef.current !== element) {
      activeAnnotationRef.current.classList.remove('practice-annotation-active');
    }

    activeAnnotationRef.current = element;
    element.classList.add('practice-annotation-active');
  };

  const handlePlayAnnotation = (annotation) => {
    const streamUrl = annotation?.streamUrl;
    if (!streamUrl) return;

    if (!annotationAudioRef.current) {
      annotationAudioRef.current = new Audio();
      annotationAudioRef.current.addEventListener('ended', () => {
        if (activeAnnotationRef.current) {
          activeAnnotationRef.current.classList.remove('practice-annotation-active');
          activeAnnotationRef.current = null;
        }
      });
    }
    const audioEl = annotationAudioRef.current;
    audioEl.pause();
    audioEl.src = streamUrl;
    audioEl.currentTime = 0;
    audioEl.play().catch(() => {});
  };

  const findAnnotationTrigger = (event) => {
    const target = event.target;
    if (target && typeof target.closest === 'function') {
      return target.closest('[data-ann-id]');
    }
    return null;
  };

  const handleAnnotationTrigger = (event) => {
    // Don't handle if clicking on option blocks or blank drop zones
    if (event.target.closest('[data-fib-option]') || event.target.closest('.fib-drop-zone')) {
      return;
    }

    if (!annotationsMap || Object.keys(annotationsMap).length === 0) {
      return;
    }

    const trigger = findAnnotationTrigger(event);
    if (!trigger) {
      return;
    }

    const annId = trigger.getAttribute('data-ann-id');
    if (!annId || !annotationsMap[annId]) {
      return;
    }

    const annotation = annotationsMap[annId];
    highlightAnnotation(trigger);
    handlePlayAnnotation(annotation);
  };

  const handleAnnotationKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    handleAnnotationTrigger(event);
  };

  const handleDragStart = (e, option) => {
    setDraggedOption(option);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, blankId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverBlank(blankId);
  };

  const handleDrop = (e, blankId) => {
    if (!draggedOption) return;
    e.preventDefault();
    
    // draggedOption is text, convert to idx
    const optionText = typeof draggedOption === 'string' ? draggedOption : draggedOption.text || draggedOption;
    const optionIdx = transformedQuestion.textToIdxMap[optionText];
    
    if (optionIdx !== undefined) {
      setUserAnswers(prev => {
        const newAnswers = { ...prev, [blankId]: optionIdx };
        return newAnswers;
      });
    }
    
    setDraggedOption(null);
    setDragOverBlank(null);
  };

  const handleOptionClick = (option) => {
    if (!transformedQuestion.blanks) return;
    
    // option is text, convert to idx
    const optionText = typeof option === 'string' ? option : option.text || option;
    const optionIdx = transformedQuestion.textToIdxMap[optionText];
    
    if (optionIdx === undefined) return;
    
    const firstEmpty = findFirstEmptyBlank(transformedQuestion.blanks, userAnswers);
    if (firstEmpty) {
      setUserAnswers(prev => ({ ...prev, [firstEmpty.blankId]: optionIdx }));
    }
  };

  const handleRemoveAnswer = (blankId) => {
    setUserAnswers(prev => {
      const newAnswers = { ...prev };
      delete newAnswers[blankId];
      return newAnswers;
    });
  };

  const handleSubmit = () => {
    if (!transformedQuestion.blanks || !checkAllBlanksFilled(transformedQuestion.blanks, userAnswers)) return;
    
    // Convert userAnswers object {blankId: idx} to array of idx in order of blanks in text
    // Example: if blanks are [blank-1, blank-2, blank-3] and userAnswers = {1: 2, 2: 1, 3: 3}
    // Then answerArray = [2, 1, 3] (idx for blank-1, blank-2, blank-3)
    const answerArray = transformedQuestion.blanks.map((blank, index) => {
      const blankId = blank.id;
      const userIdx = userAnswers[blankId];
      return userIdx;
    }).filter(idx => idx !== undefined);
    
    if (process.env.NODE_ENV === 'development') {
      // console.log('🔍 FIB Submit - Final answerArray:', {
      //   userAnswers,
      //   blanks: transformedQuestion.blanks.map(b => ({ id: b.id, correctIdx: b.correctIdx })),
      //   answerArray,
      //   textToIdxMap: transformedQuestion.textToIdxMap,
      // });
    }
    
    // Validate: compare idx with correct idx
    const allCorrect = transformedQuestion.blanks.every(blank => {
      const blankId = blank.id;
      const userIdx = userAnswers[blankId];
      const correctIdx = blank.correctIdx;
      return userIdx !== undefined && correctIdx !== null && userIdx === correctIdx;
    });
    
    if (onAnswer) {
      onAnswer({ 
        questionId: question['soal-id'] || question.id, 
        answer: answerArray, // Array of idx in order of blanks: [2, 1, 3] for example
        isCorrect: allCorrect 
      });
    }
    if (onNext) onNext();
  };

  const renderSentence = () => {
    if (!transformedQuestion.blanks || transformedQuestion.blanks.length === 0) {
      const hasAnnotations = annotationsMap && Object.keys(annotationsMap).length > 0;
      return (
        <p 
          className="question-text"
          onClick={hasAnnotations ? handleAnnotationTrigger : undefined}
          onKeyDown={hasAnnotations ? handleAnnotationKeyDown : undefined}
          dangerouslySetInnerHTML={{ __html: enhancedHtml }}
        />
      );
    }

    const parts = parseSentenceWithBlanks(transformedQuestion.text, transformedQuestion.blanks);
    if (!parts) return null;

    const hasAnnotations = annotationsMap && Object.keys(annotationsMap).length > 0;

    return (
      <div
        className="flex flex-wrap items-center gap-2 text-lg font-semibold text-[var(--text-primary)] leading-snug mb-6 max-md:text-base max-md:gap-1.5 max-md:mb-5"
        onClick={hasAnnotations ? handleAnnotationTrigger : undefined}
        onKeyDown={hasAnnotations ? handleAnnotationKeyDown : undefined}
      >
        {parts.map((part, index) => {
          if (part.type === 'text') {
            return (
              <span key={index} className="inline" dangerouslySetInnerHTML={{ __html: part.content }} />
            );
          }
          
          const blankId = part.blank.id || part.blankIndex;
          // Convert idx to text for display
          const selectedIdx = userAnswers[blankId];
          const selectedText = selectedIdx !== undefined && transformedQuestion.choicesMap[selectedIdx] 
            ? transformedQuestion.choicesMap[selectedIdx] 
            : '';
          return (
            <BlankDropZone
              key={index}
              blank={part.blank}
              blankId={blankId}
              selectedValue={selectedText}
              isDragOver={dragOverBlank === blankId}
              onDragOver={(e) => handleDragOver(e, blankId)}
              onDragLeave={() => setDragOverBlank(null)}
              onDrop={(e) => handleDrop(e, blankId)}
              onRemove={() => handleRemoveAnswer(blankId)}
            />
          );
        })}
      </div>
    );
  };

  const renderOptions = () => {
    const availableOptions = getAvailableOptions(transformedQuestion.blanks, userAnswers, transformedQuestion.textToIdxMap);
    if (availableOptions.length === 0) return null;

    return (
      <div className="mt-1 p-3 bg-[rgba(18,118,109,0.05)] dark:bg-[rgba(20,184,166,0.06)] rounded-xl max-md:p-2.5 max-md:mt-2">
        {transformedQuestion.optionsTitle && (
          <h4 className="text-base font-semibold text-[var(--text-primary)] mb-4 m-0 max-md:text-sm max-md:mb-3">
            {transformedQuestion.optionsTitle}
          </h4>
        )}
        <div className="flex flex-wrap gap-2 max-md:gap-1.5">
          {availableOptions.map((option, index) => (
            <OptionBlock
              key={index}
              option={option}
              onDragStart={handleDragStart}
              onClick={handleOptionClick}
            />
          ))}
        </div>
      </div>
    );
  };

  const allBlanksFilled = checkAllBlanksFilled(transformedQuestion.blanks, userAnswers);

  const badgeGradient = variant === 'drill'
    ? 'from-[#345d8a] to-[#1e3d6f]'
    : 'from-[#12766d] to-[#236463]';

  return (
    <motion.div
      className="flex flex-col gap-5"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <div className="flex justify-between items-center pb-4 border-b-2 border-[rgba(18,118,109,0.1)] dark:border-[var(--border-color)]">
        <motion.span
          className={`px-3.5 py-2 bg-gradient-to-br ${badgeGradient} text-white rounded-lg text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap shrink-0 max-md:text-[10px] max-md:px-2 max-md:py-1`}
          initial={{ scale: 0.85 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          Fill in the Blank
        </motion.span>
      </div>
      
      <div className="flex flex-col gap-5">
        {displayAudioUrl && (
          <QuestionAudioPlayer audioUrl={displayAudioUrl} />
        )}
        {renderSentence()}
        {renderOptions()}
      </div>

      <div className="flex justify-center pt-4 border-t-2 border-[rgba(18,118,109,0.1)] dark:border-[var(--border-color)] max-md:pt-3 max-md:pb-20 max-md:flex-col max-md:gap-3">
        <motion.button
          className="py-3 px-8 bg-gradient-to-br from-[#fcb319] to-[#f5a500] text-[var(--text-primary)] border-none rounded-xl text-base font-semibold cursor-pointer transition-all duration-300 shadow-[0_2px_4px_rgba(252,179,25,0.2)] hover:enabled:from-[#f5a500] hover:enabled:to-[#e69500] hover:enabled:shadow-[0_4px_8px_rgba(252,179,25,0.3)] hover:enabled:-translate-y-px disabled:opacity-50 disabled:cursor-not-allowed dark:text-[#1d4746] max-md:w-full max-md:py-3.5 max-md:px-6 max-md:text-[15px] max-md:min-h-[44px]"
          onClick={handleSubmit}
          disabled={!allBlanksFilled}
          whileTap={{ scale: 0.96 }}
        >
          Submit
        </motion.button>
      </div>
    </motion.div>
  );
};

export default FillInTheBlank;

